import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchAuctionCompaniesAPI,
  getRefreshTokenAPI,
  loginUserAPI,
} from "../services/authAPI";
import { persistor } from "../store";
import localforage from "localforage";
import { removeWorkOrderDetail } from "./workOrderDetailSlice";
import { removeInspectionTemplates } from "./inspectionTemplatesSlice";
import { removeWorkOrder } from "./workOrderSlice";
import { hideToaster } from "./toasterSlice";
import { jwtDecode } from "jwt-decode";
import * as Sentry from '@sentry/react'

export interface AuthState {
  token: string | null;
  userId: string | null;
  userName: string | null;
  refreshToken: string | null;
  tenant: string;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  auctionCompanies: { value: string; label: string }[];
}

const initialState: AuthState = {
  token: null,
  refreshToken: null,
  tenant: "",
  isAuthenticated: false,
  loading: false,
  error: null,
  auctionCompanies: [],
  userId: null,
  userName: null,
};

export const fetchAuctionCompanies = createAsyncThunk(
  "auth/fetchAuctionCompanies",
  async (email: string, { rejectWithValue }) => {
    try {
      const auctionCompanies = await fetchAuctionCompaniesAPI(email);
      return auctionCompanies;
    } catch (error: any) {
      console.log("[Error in Fetching Auction Companies]:: ", error);
      return rejectWithValue(
        error.message || "Failed to fetch auction companies"
      );
    }
  }
);

export const logoutAndClearStorage = createAsyncThunk(
  "auth/logoutAndClearStorage",
  async (_, { dispatch }) => {
    dispatch(logout());

    dispatch(removeInspectionTemplates());
    dispatch(removeWorkOrderDetail());
    dispatch(removeWorkOrder());
    dispatch(hideToaster());
    await localStorage.clear();
    await localforage.clear();
    await persistor.purge();
  }
);

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (
    {
      email,
      password,
      auctionCompany,
    }: { email: string; password: string; auctionCompany: string },
    { rejectWithValue }
  ) => {
    try {
      const { token, refreshToken, userName, userId } = await loginUserAPI(
        email,
        password,
        auctionCompany
      );
      return { token, auctionCompany, refreshToken, userName, userId };
    } catch (error: any) {
      console.log("[Error in Login]:: ", error);
      return rejectWithValue(error.message || "Login failed");
    }
  }
);

export const checkToken = createAsyncThunk(
  "auth/checkToken",
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as { auth: AuthState };
      const token = state.auth.token;
      if (token) {
        const decoded: any = jwtDecode(token);

        // Calculate time until expiration in milliseconds
        const timeUntilExpiry = decoded.exp * 1000 - Date.now();
        console.log("Time until expiry: ", timeUntilExpiry);
        // Refresh if token is expired OR will expire in the next 5 minutes
        if (timeUntilExpiry < 5 * 60 * 1000) {
          const refreshToken = state.auth.refreshToken;
          const tenant = state.auth.tenant;
          if (refreshToken && tenant) {
            console.log("Refreshing token");
            try {
              await dispatch(
                getRefreshToken({ RefreshToken: refreshToken, Tenant: tenant })
              );
            } catch (refreshError) {
              // If refresh fails, log the user out
              dispatch(logoutAndClearStorage());
              throw refreshError;
            }
          } else {
            // If we don't have refresh token or tenant, log out
            dispatch(logoutAndClearStorage());
            throw new Error("RefreshToken or Tenant is missing.");
          }
        }
      }
    } catch (error: any) {
      console.log("[Error in checkToken]:: ", error);
      return rejectWithValue(error.message || "Token validation failed");
    }
  }
);

export const getRefreshToken = createAsyncThunk(
  "auth/getRefreshToken",
  async (
    { RefreshToken, Tenant }: { RefreshToken: string; Tenant: string },
    { rejectWithValue }
  ) => {
    try {
      const { token, refreshToken } = await getRefreshTokenAPI(
        RefreshToken,
        Tenant
      );
      return { token, refreshToken };
    } catch (error: any) {
      console.log("[Error in Login]:: ", error);
      return rejectWithValue(error.message || "Login failed");
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.token = null;
      state.refreshToken = null;
      state.tenant = "";
      state.isAuthenticated = false;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAuctionCompanies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAuctionCompanies.fulfilled,
        (state, action: PayloadAction<{ value: string; label: string }[]>) => {
          state.loading = false;
          state.auctionCompanies = action.payload;
        }
      )
      .addCase(fetchAuctionCompanies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        loginUser.fulfilled,
        (
          state,
          action: PayloadAction<{
            token: string;
            refreshToken: string;
            auctionCompany: string;
            userId: string;
            userName: string;
          }>
        ) => {
          state.loading = false;
          state.token = action.payload.token;
          state.refreshToken = action.payload.refreshToken;
          state.tenant = action.payload.auctionCompany;
          state.isAuthenticated = true;
          state.userId = action.payload.userId;
          state.userName = action.payload.userName;
          Sentry.setContext("loginEvent", {
            timestamp: new Date().toISOString(),
            userId: action.payload.userId,
            tenant: action.payload.auctionCompany,
            userName: action.payload.userName,
          });
        }
      )
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(
        getRefreshToken.fulfilled,
        (
          state,
          action: PayloadAction<{ token: string; refreshToken: string }>
        ) => {
          state.token = action.payload.token;
          state.refreshToken = action.payload.refreshToken;
        }
      )
      .addCase(getRefreshToken.rejected, (state) => {
        // If token refresh fails, we should log the user out
        state.token = null;
        state.refreshToken = null;
        state.tenant = "";
        state.isAuthenticated = false;
      });
  },
});

export const { logout, clearError } = authSlice.actions;
export default authSlice.reducer;
